import { generateDeviceKeys, toBase64, type DeviceKeys } from '@trove/crypto';
import { postJson } from './api';
import { formatPairingCode } from './pairingCode';

const encoder = new TextEncoder();

export type DeviceAuthorization = {
  code: string;
  deviceId: string;
  verificationUri: string;
  verificationUriComplete: string;
  expiresAt: string;
  interval: number;
};

export type PairingSession = {
  authorization: DeviceAuthorization;
  deviceKeys: DeviceKeys;
  pollToken: string;
};

const sha256Hex = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export function browserDeviceLabel(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua) || /CriOS\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua) || /FxiOS\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const device = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'device';
  return `${browser} on ${device}`;
}

export async function startDevicePairing(): Promise<PairingSession> {
  const deviceKeys = generateDeviceKeys();
  const pollTokenBytes = crypto.getRandomValues(new Uint8Array(32));
  const pollToken = [...pollTokenBytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  const authorization = await postJson<DeviceAuthorization>('/v1/auth/device', {
    label: browserDeviceLabel(),
    encryptionPublicKey: toBase64(deviceKeys.encryptionPublicKey),
    signingPublicKey: toBase64(deviceKeys.signingPublicKey),
    pollTokenHash: await sha256Hex(pollToken),
  });
  return { authorization, deviceKeys, pollToken };
}

export async function pollDevicePairing(
  session: PairingSession,
): Promise<{ status: string; deviceId?: string }> {
  return postJson<{ status: string; deviceId?: string; token?: string }>('/v1/auth/device/poll', {
    code: session.authorization.code,
    pollToken: session.pollToken,
  });
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const ACTIVE_PAIRING_KEY = 'trove-active-pairing';

function pairingStorage() {
  try {
    if (typeof localStorage === 'undefined') return undefined;
    return localStorage;
  } catch {
    return undefined;
  }
}

export type ActivePairing = {
  code: string;
  deviceId: string;
  expiresAt: string;
};

export function rememberActivePairing(pairing: ActivePairing) {
  pairingStorage()?.setItem(ACTIVE_PAIRING_KEY, JSON.stringify(pairing));
}

export function readActivePairing(): ActivePairing | undefined {
  try {
    const raw = pairingStorage()?.getItem(ACTIVE_PAIRING_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as ActivePairing;
    if (parsed.expiresAt && Date.now() > new Date(parsed.expiresAt).getTime()) {
      pairingStorage()?.removeItem(ACTIVE_PAIRING_KEY);
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function clearActivePairing(code?: string) {
  if (code) {
    const current = readActivePairing();
    if (current && formatPairingCode(current.code) !== formatPairingCode(code)) return;
  }
  pairingStorage()?.removeItem(ACTIVE_PAIRING_KEY);
}

export function isOwnPairingCode(code: string) {
  const active = readActivePairing();
  return Boolean(active && formatPairingCode(active.code) === formatPairingCode(code));
}

export function isCliDeviceLabel(label: string) {
  return /\(CLI\)\s*$/.test(label);
}
